/**
 * Rose Widget Storage Manager
 *
 * Centralized localStorage management for Rose widget.
 * All data stored under a single "rose" key with structured namespaces.
 *
 * Features:
 * - Single localStorage key: "rose"
 * - Type-safe access to all storage
 * - Automatic migration from legacy keys
 * - Zero data loss backward compatibility
 */

import { logger } from '../utils/core/logger';
import { getLocalStorageIfAvailable } from '../utils/web/storageAvailability';
import { subscribeConsentMode } from './consentMode';

// ==================== TYPE DEFINITIONS ====================

interface WidgetSettings {
    logLevel: string | null; // Default log level (from 'loglevel')
    namedLogLevels: Record<string, string>; // Named logger levels (e.g., 'Rose')
}

export interface TrafficControlData {
    clientId: string;
    trafficAllocated: number;
    enabled: boolean;
    bucket: number;
    timestamp: number;
}

interface WidgetAnalytics {
    lastSessionId: string | null;
    lastActiveSessionId: string | null; // Session where user sent at least one message
    lastActiveSessionDate: number | null; // Unix timestamp (milliseconds) when last session was active
    clientId: string | null; // Stable client ID for traffic control bucketing (PostHog distinct_id or UUID)
    apiVersion: string | null; // API version for cross-session tracking
    frontendVersion: string | null; // Frontend version for cross-session tracking
}

interface WidgetData {
    settings: WidgetSettings;
    analytics: WidgetAnalytics;
    trafficControl: TrafficControlData | null; // Traffic allocation decision cache
    trafficControlOverride: boolean | null; // Manual override: true=force show, false=force hide, null=no override
    completedPostConversion: Record<string, boolean>; // Per-domain post-conversion completion flags
}

interface ClientData {
    id: string;
    name: string;
    /** Where the workspace lives, shown next to its name: `example.com` or `host/path`. */
    location: string;
    domain: string;
    siteName: string;
    websiteUrl: string;
    /** Root page the workspace's widget stands in for (`PreviewHost.pageUrl`). */
    pageUrl?: string;
    isLocalPreview: boolean;
}

interface ClientCache {
    clients: ClientData[];
    timestamp: number;
}

interface PreprodConfig {
    apiKey?: string;
    apiUrl: string;
    environment: 'development' | 'staging' | 'production';
}

interface PreprodEndpoint {
    selectedId: string;
    customUrl: string;
    useCustom: boolean;
}

interface PreprodClient {
    selected: ClientData | null;
    cache: Record<string, ClientCache>;
}

interface PreprodIframe {
    customSettings: Record<string, boolean>;
    customUrls: Record<string, string>;
    skipList: string[]; // Legacy, for migration only
}

interface PreprodData {
    config: PreprodConfig;
    endpoint: PreprodEndpoint;
    client: PreprodClient;
    iframe: PreprodIframe;
    visitorIp?: string;
    visitorIpDomain?: string;
    revealSimulationEnabled?: boolean;
    rb2bSimulationEnabled?: boolean;
    rb2bSimulationIp?: string;
}

interface BackofficeTimeRange {
    preset: string | null;
    customRange: { from: string; to: string } | null;
}

interface BackofficeData {
    selectedDomain: string | null;
    selectedEnvironment: string | null;
    timeRange: BackofficeTimeRange;
    // Generic storage for page-specific preferences (filters, columns, widths)
    // Keys follow pattern: 'accounts-filters', 'visitors-visible-columns', etc.
    preferences: Record<string, string>;
}

interface RoseStorage {
    version: number;
    widget: WidgetData;
    preprod?: PreprodData; // Optional - only exists in playground environment
    backoffice?: BackofficeData; // Optional - only exists in client-backoffice environment
}

// ==================== CONSTANTS ====================

const STORAGE_KEY = 'rose';
const CURRENT_VERSION = 1;

// Legacy keys for migration
const LEGACY_KEYS = {
    EVALUATIONS: 'llm_evaluations',
    CONVERSATIONS: 'llm_conversations',
    PREPROD_CONFIG: 'inboundx_preprod_config',
    SELECTED_ENDPOINT: 'inboundx_selected_endpoint',
    CUSTOM_ENDPOINT: 'inboundx_custom_endpoint',
    USE_CUSTOM_ENDPOINT: 'inboundx_use_custom_endpoint',
    SELECTED_CLIENT: 'inboundx_selected_client',
} as const;

// ==================== DEFAULT VALUES ====================

const DEFAULT_WIDGET_DATA: WidgetData = {
    settings: {
        logLevel: null,
        namedLogLevels: {},
    },
    analytics: {
        lastSessionId: null,
        lastActiveSessionId: null,
        lastActiveSessionDate: null,
        clientId: null,
        apiVersion: null,
        frontendVersion: null,
    },
    completedPostConversion: {},
    trafficControl: null,
    trafficControlOverride: null,
};

const DEFAULT_PREPROD_DATA: PreprodData = {
    config: {
        apiUrl: '',
        environment: 'development',
    },
    endpoint: {
        selectedId: '',
        customUrl: '',
        useCustom: false,
    },
    client: {
        selected: null,
        cache: {},
    },
    iframe: {
        customSettings: {},
        customUrls: {},
        skipList: [],
    },
};

const DEFAULT_BACKOFFICE_DATA: BackofficeData = {
    selectedDomain: null,
    selectedEnvironment: null,
    timeRange: {
        preset: null,
        customRange: null,
    },
    preferences: {},
};

const DEFAULT_STORAGE: RoseStorage = {
    version: CURRENT_VERSION,
    widget: DEFAULT_WIDGET_DATA,
    // preprod is optional - only created when needed in playground
};

// ==================== STORAGE MANAGER CLASS ====================

class RoseStorageManager {
    private storage: RoseStorage | null = null;
    // Broken browser storage (e.g. Firefox NS_ERROR_FAILURE) fails on every write;
    // report it once per page instead of once per update (ROSE-FRONT-1MD).
    private persistenceFailureReported = false;
    private migrated = false;

    constructor() {
        subscribeConsentMode((mode, previous) => {
            if (mode !== 'full' && previous === 'full') {
                // init({consentMode}) may run after an import read the old full-mode cache.
                this.storage = null;
                this.migrated = false;
            }
        });
    }

    private reportPersistenceFailure(message: string, error: unknown): void {
        if (this.persistenceFailureReported) {
            logger.debug(message, error);
            return;
        }
        this.persistenceFailureReported = true;
        logger.warn(message, error);
    }

    /**
     * Get default storage structure
     */
    private getDefaults(): RoseStorage {
        return {
            version: CURRENT_VERSION,
            widget: {
                settings: {
                    logLevel: null,
                    namedLogLevels: {},
                },
                analytics: {
                    lastSessionId: null,
                    lastActiveSessionId: null,
                    lastActiveSessionDate: null,
                    clientId: null,
                    apiVersion: null,
                    frontendVersion: null,
                },
                completedPostConversion: {},
                trafficControl: null,
                trafficControlOverride: null,
            },
        };
    }

    /**
     * Get the complete storage object
     */
    private getStorage(): RoseStorage {
        if (this.storage) {
            return this.storage;
        }

        const localStorageRef = getLocalStorageIfAvailable();

        // Android WebViews can expose `window.localStorage` as null.
        // In that case, keep working with in-memory defaults.
        if (!localStorageRef) {
            logger.debug(
                'localStorage not available (service worker/webview context), using defaults'
            );
            this.storage = this.getDefaults();
            return this.storage;
        }

        try {
            const stored = localStorageRef.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored) as RoseStorage;
                this.storage = this.validateAndMerge(parsed);
            } else {
                // No existing storage, check for legacy data
                this.storage = this.migrateFromLegacy();
            }
        } catch (error) {
            this.reportPersistenceFailure('Failed to load Rose storage, using defaults:', error);
            this.storage = { ...DEFAULT_STORAGE };
        }

        this.saveStorage();
        return this.storage;
    }

    /**
     * Validate and merge stored data with defaults
     */
    private validateAndMerge(stored: Partial<RoseStorage>): RoseStorage {
        const result: RoseStorage = {
            version: stored.version || CURRENT_VERSION,
            widget: {
                settings: {
                    ...DEFAULT_WIDGET_DATA.settings,
                    ...stored.widget?.settings,
                    namedLogLevels: {
                        ...DEFAULT_WIDGET_DATA.settings.namedLogLevels,
                        ...stored.widget?.settings?.namedLogLevels,
                    },
                },
                analytics: {
                    ...DEFAULT_WIDGET_DATA.analytics,
                    ...stored.widget?.analytics,
                },
                completedPostConversion: stored.widget?.completedPostConversion || {},
                trafficControl: stored.widget?.trafficControl || null,
                trafficControlOverride: stored.widget?.trafficControlOverride ?? null,
            },
        };

        // Only include preprod data if it exists in stored data
        if (stored.preprod) {
            result.preprod = {
                ...DEFAULT_PREPROD_DATA,
                ...stored.preprod,
                config: {
                    ...DEFAULT_PREPROD_DATA.config,
                    ...stored.preprod?.config,
                },
                endpoint: {
                    ...DEFAULT_PREPROD_DATA.endpoint,
                    ...stored.preprod?.endpoint,
                },
                client: {
                    selected: stored.preprod?.client?.selected || null,
                    cache: stored.preprod?.client?.cache || {},
                },
                iframe: {
                    ...DEFAULT_PREPROD_DATA.iframe,
                    ...stored.preprod?.iframe,
                },
            };
        }

        // Only include backoffice data if it exists in stored data
        if (stored.backoffice) {
            result.backoffice = {
                ...DEFAULT_BACKOFFICE_DATA,
                ...stored.backoffice,
                timeRange: {
                    ...DEFAULT_BACKOFFICE_DATA.timeRange,
                    ...stored.backoffice?.timeRange,
                },
                preferences: stored.backoffice?.preferences || {},
            };
        }

        return result;
    }

    /**
     * Check if any legacy preprod keys exist
     */
    private hasLegacyPreprodData(): boolean {
        const localStorageRef = getLocalStorageIfAvailable();
        if (!localStorageRef) return false;

        try {
            if (localStorageRef.getItem(LEGACY_KEYS.PREPROD_CONFIG)) return true;
            if (localStorageRef.getItem(LEGACY_KEYS.SELECTED_ENDPOINT)) return true;
            if (localStorageRef.getItem(LEGACY_KEYS.CUSTOM_ENDPOINT)) return true;
            if (localStorageRef.getItem(LEGACY_KEYS.USE_CUSTOM_ENDPOINT)) return true;
            if (localStorageRef.getItem(LEGACY_KEYS.SELECTED_CLIENT)) return true;

            // Check for client cache keys
            for (let i = 0; i < localStorageRef.length; i++) {
                const key = localStorageRef.key(i);
                if (key?.startsWith('inboundx_clients_cache_')) {
                    return true;
                }
            }
        } catch {
            // Silently fail
        }
        return false;
    }

    /**
     * Check if any legacy backoffice keys exist (rose:* pattern)
     */
    private hasLegacyBackofficeData(): boolean {
        const localStorageRef = getLocalStorageIfAvailable();
        if (!localStorageRef) return false;

        try {
            for (let i = 0; i < localStorageRef.length; i++) {
                const key = localStorageRef.key(i);
                // Match rose:* keys but not the main 'rose' key
                if (key?.startsWith('rose:')) {
                    return true;
                }
            }
        } catch {
            // Silently fail
        }
        return false;
    }

    /**
     * Migrate data from legacy localStorage keys
     */
    private migrateFromLegacy(): RoseStorage {
        if (this.migrated) {
            return { ...DEFAULT_STORAGE };
        }

        logger.debug('🔄 Migrating from legacy localStorage keys...');

        const migrated: RoseStorage = {
            version: CURRENT_VERSION,
            widget: { ...DEFAULT_WIDGET_DATA },
        };

        // Only migrate preprod data if legacy preprod keys exist (playground only)
        if (this.hasLegacyPreprodData()) {
            migrated.preprod = this.migratePreprodData();
            logger.debug('  Migrated preprod data (playground environment detected)');
        }

        // Only migrate backoffice data if legacy rose:* keys exist (client-backoffice only)
        if (this.hasLegacyBackofficeData()) {
            migrated.backoffice = this.migrateBackofficeData();
            logger.debug('  Migrated backoffice data (client-backoffice environment detected)');
        }

        this.migrated = true;

        logger.debug('✅ Migration completed successfully');
        return migrated;
    }

    /**
     * Migrate preprod data
     */
    private migratePreprodData(): PreprodData {
        const preprod = { ...DEFAULT_PREPROD_DATA };
        const localStorageRef = getLocalStorageIfAvailable();
        if (!localStorageRef) return preprod;

        // Migrate preprod config
        try {
            const configData = localStorageRef.getItem(LEGACY_KEYS.PREPROD_CONFIG);
            if (configData) {
                const config = JSON.parse(configData);
                preprod.config = {
                    apiKey: config.apiKey,
                    apiUrl: config.apiUrl || '',
                    environment: config.environment || 'development',
                };
                preprod.iframe = {
                    customSettings: config.customIframeSettings || {},
                    customUrls: config.customSiteUrls || {},
                    skipList: config.skipIframeForSites || [],
                };
                logger.debug('  Migrated preprod config');
            }
        } catch (error) {
            logger.debug('  Failed to migrate preprod config:', error);
        }

        // Migrate endpoint
        try {
            const endpoint = localStorageRef.getItem(LEGACY_KEYS.SELECTED_ENDPOINT);
            if (endpoint) {
                const parsed = JSON.parse(endpoint);
                preprod.endpoint.selectedId = parsed.id || '';
                logger.debug('  Migrated endpoint');
            }

            const customUrl = localStorageRef.getItem(LEGACY_KEYS.CUSTOM_ENDPOINT);
            if (customUrl) {
                preprod.endpoint.customUrl = customUrl;
            }

            const useCustom = localStorageRef.getItem(LEGACY_KEYS.USE_CUSTOM_ENDPOINT);
            if (useCustom) {
                preprod.endpoint.useCustom = JSON.parse(useCustom);
            }
        } catch (error) {
            logger.debug('  Failed to migrate endpoint:', error);
        }

        // Migrate client
        try {
            const client = localStorageRef.getItem(LEGACY_KEYS.SELECTED_CLIENT);
            if (client) {
                preprod.client.selected = JSON.parse(client);
                logger.debug('  Migrated selected client');
            }
        } catch (error) {
            logger.debug('  Failed to migrate client:', error);
        }

        // Migrate client cache (inboundx_clients_cache_*)
        try {
            for (let i = 0; i < localStorageRef.length; i++) {
                const key = localStorageRef.key(i);
                if (key?.startsWith('inboundx_clients_cache_')) {
                    const apiUrl = key.replace('inboundx_clients_cache_', '');
                    const cacheData = localStorageRef.getItem(key);
                    if (cacheData) {
                        preprod.client.cache[apiUrl] = JSON.parse(cacheData);
                        logger.debug(`  Migrated client cache for ${apiUrl}`);
                    }
                }
            }
        } catch (error) {
            logger.debug('  Failed to migrate client cache:', error);
        }

        return preprod;
    }

    /**
     * Migrate backoffice data from legacy rose:* keys
     */
    private migrateBackofficeData(): BackofficeData {
        const backoffice = { ...DEFAULT_BACKOFFICE_DATA };
        const localStorageRef = getLocalStorageIfAvailable();
        if (!localStorageRef) return backoffice;

        // Migrate selected domain
        try {
            const domain = localStorageRef.getItem('rose:selected-domain');
            if (domain) {
                backoffice.selectedDomain = domain;
                logger.debug(`  Migrated backoffice selected domain: ${domain}`);
            }
        } catch (error) {
            logger.debug('  Failed to migrate backoffice selected domain:', error);
        }

        // Migrate selected environment
        try {
            const environment = localStorageRef.getItem('rose:selected-environment');
            if (environment) {
                backoffice.selectedEnvironment = environment;
                logger.debug(`  Migrated backoffice selected environment: ${environment}`);
            }
        } catch (error) {
            logger.debug('  Failed to migrate backoffice selected environment:', error);
        }

        // Migrate time range
        try {
            const timeRange = localStorageRef.getItem('rose:stats-time-range');
            if (timeRange) {
                backoffice.timeRange.preset = timeRange;
                logger.debug(`  Migrated backoffice time range preset: ${timeRange}`);
            }

            const customRange = localStorageRef.getItem('rose:stats-custom-range');
            if (customRange) {
                backoffice.timeRange.customRange = JSON.parse(customRange);
                logger.debug('  Migrated backoffice custom time range');
            }
        } catch (error) {
            logger.debug('  Failed to migrate backoffice time range:', error);
        }

        // Migrate all other rose:* keys as preferences
        try {
            for (let i = 0; i < localStorageRef.length; i++) {
                const key = localStorageRef.key(i);
                if (key?.startsWith('rose:')) {
                    // Skip already handled keys
                    if (
                        key === 'rose:selected-domain' ||
                        key === 'rose:selected-environment' ||
                        key === 'rose:stats-time-range' ||
                        key === 'rose:stats-custom-range'
                    ) {
                        continue;
                    }
                    // Store other keys as preferences (remove rose: prefix)
                    const prefKey = key.replace('rose:', '');
                    const value = localStorageRef.getItem(key);
                    if (value) {
                        backoffice.preferences[prefKey] = value;
                        logger.debug(`  Migrated backoffice preference: ${prefKey}`);
                    }
                }
            }
        } catch (error) {
            logger.debug('  Failed to migrate backoffice preferences:', error);
        }

        return backoffice;
    }

    /**
     * Save storage to localStorage
     */
    private saveStorage(): void {
        if (!this.storage) return;

        const localStorageRef = getLocalStorageIfAvailable();
        if (!localStorageRef) {
            logger.debug(
                'localStorage not available (service worker/webview context), skipping save'
            );
            return;
        }

        try {
            localStorageRef.setItem(STORAGE_KEY, JSON.stringify(this.storage));
        } catch (error) {
            // Handle quota exceeded error gracefully - widget continues working,
            // just loses persistence for this session
            if (error instanceof DOMException && error.name === 'QuotaExceededError') {
                logger.warn('localStorage quota exceeded, continuing without persistence');
                return;
            }
            this.reportPersistenceFailure(
                'Failed to save Rose storage, continuing without persistence:',
                error
            );
        }
    }

    /**
     * Update a specific path in storage
     */
    private updatePath<T>(path: string[], updater: (current: T) => T): void {
        const storage = this.getStorage();

        // Navigate to the target
        // biome-ignore lint/suspicious/noExplicitAny: dynamic path traversal requires any
        let current: any = storage;
        for (let i = 0; i < path.length - 1; i++) {
            current = current[path[i]];
        }

        // Update the value
        const lastKey = path[path.length - 1];
        current[lastKey] = updater(current[lastKey]);

        this.storage = storage;
        this.saveStorage();
    }

    // ==================== PUBLIC API - WIDGET SETTINGS ====================

    getLogLevel(): string | null {
        return this.getStorage().widget.settings.logLevel;
    }

    setLogLevel(level: string | null): void {
        this.updatePath(['widget', 'settings', 'logLevel'], () => level);
    }

    getNamedLogLevel(name: string): string | null {
        return this.getStorage().widget.settings.namedLogLevels[name] || null;
    }

    setNamedLogLevel(name: string, level: string): void {
        this.updatePath(['widget', 'settings', 'namedLogLevels', name], () => level);
    }

    clearNamedLogLevel(name: string): void {
        this.updatePath(
            ['widget', 'settings', 'namedLogLevels'],
            (current: Record<string, string>) => {
                const updated = { ...current };
                delete updated[name];
                return updated;
            }
        );
    }

    // ==================== PUBLIC API - WIDGET ANALYTICS ====================

    getLastSessionId(): string | null {
        return this.getStorage().widget.analytics.lastSessionId;
    }

    setLastSessionId(sessionId: string | null): void {
        this.updatePath(['widget', 'analytics', 'lastSessionId'], () => sessionId);
    }

    getLastActiveSessionId(): string | null {
        return this.getStorage().widget.analytics.lastActiveSessionId;
    }

    setLastActiveSessionId(sessionId: string | null): void {
        this.updatePath(['widget', 'analytics', 'lastActiveSessionId'], () => sessionId);
    }

    getLastActiveSessionDate(): number | null {
        return this.getStorage().widget.analytics.lastActiveSessionDate;
    }

    setLastActiveSessionDate(timestamp: number | null): void {
        this.updatePath(['widget', 'analytics', 'lastActiveSessionDate'], () => timestamp);
    }

    getAnalyticsClientId(): string | null {
        return this.getStorage().widget.analytics.clientId;
    }

    setAnalyticsClientId(clientId: string | null): void {
        this.updatePath(['widget', 'analytics', 'clientId'], () => clientId);
    }

    getApiVersion(): string | null {
        return this.getStorage().widget.analytics.apiVersion;
    }

    setApiVersion(version: string | null): void {
        this.updatePath(['widget', 'analytics', 'apiVersion'], () => version);
    }

    getFrontendVersion(): string | null {
        return this.getStorage().widget.analytics.frontendVersion;
    }

    setFrontendVersion(version: string | null): void {
        this.updatePath(['widget', 'analytics', 'frontendVersion'], () => version);
    }

    // ==================== PUBLIC API - POST-CONVERSION COMPLETION ====================

    isPostConversionComplete(domain: string): boolean {
        return this.getStorage().widget.completedPostConversion?.[domain] === true;
    }

    setPostConversionComplete(domain: string): void {
        this.updatePath(['widget', 'completedPostConversion', domain], () => true);
    }

    clearPostConversionComplete(domain: string): void {
        const storage = this.getStorage();
        const updated = { ...storage.widget.completedPostConversion };
        delete updated[domain];
        this.updatePath(['widget', 'completedPostConversion'], () => updated);
    }

    // ==================== PUBLIC API - TRAFFIC CONTROL ====================

    getTrafficControl(): TrafficControlData | null {
        return this.getStorage().widget.trafficControl;
    }

    setTrafficControl(data: TrafficControlData): void {
        this.updatePath(['widget', 'trafficControl'], () => data);
    }

    clearTrafficControl(): void {
        this.updatePath(['widget', 'trafficControl'], () => null);
    }

    getTrafficControlOverride(): boolean | null {
        return this.getStorage().widget.trafficControlOverride;
    }

    setTrafficControlOverride(value: boolean | null): void {
        this.updatePath(['widget', 'trafficControlOverride'], () => value);
    }

    clearTrafficControlOverride(): void {
        this.updatePath(['widget', 'trafficControlOverride'], () => null);
    }

    // ==================== PUBLIC API - PREPROD CONFIG ====================

    /**
     * Ensure preprod data exists (create if needed for playground)
     */
    private ensurePreprodData(): void {
        const storage = this.getStorage();
        if (!storage.preprod) {
            storage.preprod = { ...DEFAULT_PREPROD_DATA };
            this.storage = storage;
            this.saveStorage();
        }
    }

    getPreprodConfig(): PreprodConfig {
        this.ensurePreprodData();
        // biome-ignore lint/style/noNonNullAssertion: ensurePreprodData guarantees preprod exists
        return { ...this.getStorage().preprod!.config };
    }

    updatePreprodConfig(updates: Partial<PreprodConfig>): void {
        this.ensurePreprodData();
        this.updatePath(['preprod', 'config'], (current: PreprodConfig) => ({
            ...current,
            ...updates,
        }));
    }

    // ==================== PUBLIC API - PREPROD ENDPOINT ====================

    getPreprodEndpoint(): PreprodEndpoint {
        this.ensurePreprodData();
        // biome-ignore lint/style/noNonNullAssertion: ensurePreprodData guarantees preprod exists
        return { ...this.getStorage().preprod!.endpoint };
    }

    updatePreprodEndpoint(updates: Partial<PreprodEndpoint>): void {
        this.ensurePreprodData();
        this.updatePath(['preprod', 'endpoint'], (current: PreprodEndpoint) => ({
            ...current,
            ...updates,
        }));
    }

    // ==================== PUBLIC API - PREPROD CLIENT ====================

    getSelectedClient(): ClientData | null {
        this.ensurePreprodData();
        // biome-ignore lint/style/noNonNullAssertion: ensurePreprodData guarantees preprod exists
        return this.getStorage().preprod!.client.selected;
    }

    setSelectedClient(client: ClientData | null): void {
        this.ensurePreprodData();
        this.updatePath(['preprod', 'client', 'selected'], () => client);
    }

    getClientCache(apiUrl: string): ClientCache | null {
        this.ensurePreprodData();
        // biome-ignore lint/style/noNonNullAssertion: ensurePreprodData guarantees preprod exists
        return this.getStorage().preprod!.client.cache[apiUrl] || null;
    }

    setClientCache(apiUrl: string, cache: ClientCache): void {
        this.ensurePreprodData();
        this.updatePath(['preprod', 'client', 'cache', apiUrl], () => cache);
    }

    clearClientCache(apiUrl?: string): void {
        this.ensurePreprodData();
        if (apiUrl) {
            this.updatePath(
                ['preprod', 'client', 'cache'],
                (current: Record<string, ClientCache>) => {
                    const updated = { ...current };
                    delete updated[apiUrl];
                    return updated;
                }
            );
        } else {
            this.updatePath(['preprod', 'client', 'cache'], () => ({}));
        }
    }

    // ==================== PUBLIC API - PREPROD IFRAME ====================

    getIframeSettings(): Record<string, boolean> {
        this.ensurePreprodData();
        // biome-ignore lint/style/noNonNullAssertion: ensurePreprodData guarantees preprod exists
        return { ...this.getStorage().preprod!.iframe.customSettings };
    }

    setIframeSetting(domain: string, allowed: boolean): void {
        this.ensurePreprodData();
        this.updatePath(['preprod', 'iframe', 'customSettings', domain], () => allowed);
    }

    clearIframeSetting(domain: string): void {
        this.ensurePreprodData();
        this.updatePath(
            ['preprod', 'iframe', 'customSettings'],
            (current: Record<string, boolean>) => {
                const updated = { ...current };
                delete updated[domain];
                return updated;
            }
        );
    }

    getCustomUrls(): Record<string, string> {
        this.ensurePreprodData();
        // biome-ignore lint/style/noNonNullAssertion: ensurePreprodData guarantees preprod exists
        return { ...this.getStorage().preprod!.iframe.customUrls };
    }

    setCustomUrl(key: string, url: string): void {
        this.ensurePreprodData();
        this.updatePath(['preprod', 'iframe', 'customUrls', key], () => url);
    }

    clearCustomUrl(key: string): void {
        this.ensurePreprodData();
        this.updatePath(['preprod', 'iframe', 'customUrls'], (current: Record<string, string>) => {
            const updated = { ...current };
            delete updated[key];
            return updated;
        });
    }

    // ==================== PUBLIC API - PREPROD VISITOR IP ====================

    getVisitorIp(): string | null {
        this.ensurePreprodData();
        const storage = this.getStorage();
        return storage.preprod?.visitorIp || null;
    }

    setVisitorIp(ip: string): void {
        this.ensurePreprodData();
        this.updatePath(['preprod', 'visitorIp'], () => ip);
    }

    getVisitorIpDomain(): string | null {
        this.ensurePreprodData();
        const storage = this.getStorage();
        return storage.preprod?.visitorIpDomain || null;
    }

    setVisitorIpDomain(domain: string): void {
        this.ensurePreprodData();
        this.updatePath(['preprod', 'visitorIpDomain'], () => domain);
    }

    // ==================== PUBLIC API - PREPROD REVEAL SIMULATION ====================

    getRevealSimulationEnabled(): boolean {
        this.ensurePreprodData();
        const storage = this.getStorage();
        return storage.preprod?.revealSimulationEnabled || false;
    }

    setRevealSimulationEnabled(enabled: boolean): void {
        this.ensurePreprodData();
        this.updatePath(['preprod', 'revealSimulationEnabled'], () => enabled);
    }

    // ==================== PUBLIC API - PREPROD RB2B (US VISITOR) SIMULATION ====================

    getRb2bSimulationEnabled(): boolean {
        this.ensurePreprodData();
        const storage = this.getStorage();
        return storage.preprod?.rb2bSimulationEnabled || false;
    }

    setRb2bSimulationEnabled(enabled: boolean): void {
        this.ensurePreprodData();
        this.updatePath(['preprod', 'rb2bSimulationEnabled'], () => enabled);
    }

    getRb2bSimulationIp(): string {
        this.ensurePreprodData();
        const storage = this.getStorage();
        return storage.preprod?.rb2bSimulationIp || '';
    }

    setRb2bSimulationIp(ip: string): void {
        this.ensurePreprodData();
        this.updatePath(['preprod', 'rb2bSimulationIp'], () => ip);
    }

    // ==================== PUBLIC API - BACKOFFICE ====================

    /**
     * Ensure backoffice data exists (create if needed for client-backoffice)
     */
    private ensureBackofficeData(): void {
        const storage = this.getStorage();
        if (!storage.backoffice) {
            storage.backoffice = { ...DEFAULT_BACKOFFICE_DATA };
            this.storage = storage;
            this.saveStorage();
        }
    }

    getBackofficeSelectedDomain(): string | null {
        this.ensureBackofficeData();
        // biome-ignore lint/style/noNonNullAssertion: ensureBackofficeData guarantees backoffice exists
        return this.getStorage().backoffice!.selectedDomain;
    }

    setBackofficeSelectedDomain(domain: string | null): void {
        this.ensureBackofficeData();
        this.updatePath(['backoffice', 'selectedDomain'], () => domain);
    }

    getBackofficeSelectedEnvironment(): string | null {
        this.ensureBackofficeData();
        // biome-ignore lint/style/noNonNullAssertion: ensureBackofficeData guarantees backoffice exists
        return this.getStorage().backoffice!.selectedEnvironment;
    }

    setBackofficeSelectedEnvironment(environment: string | null): void {
        this.ensureBackofficeData();
        this.updatePath(['backoffice', 'selectedEnvironment'], () => environment);
    }

    getBackofficeTimeRange(): BackofficeTimeRange {
        this.ensureBackofficeData();
        // biome-ignore lint/style/noNonNullAssertion: ensureBackofficeData guarantees backoffice exists
        return { ...this.getStorage().backoffice!.timeRange };
    }

    setBackofficeTimeRange(timeRange: Partial<BackofficeTimeRange>): void {
        this.ensureBackofficeData();
        this.updatePath(['backoffice', 'timeRange'], (current: BackofficeTimeRange) => ({
            ...current,
            ...timeRange,
        }));
    }

    getBackofficePreference(key: string): string | null {
        this.ensureBackofficeData();
        // biome-ignore lint/style/noNonNullAssertion: ensureBackofficeData guarantees backoffice exists
        return this.getStorage().backoffice!.preferences[key] || null;
    }

    setBackofficePreference(key: string, value: string): void {
        this.ensureBackofficeData();
        this.updatePath(['backoffice', 'preferences', key], () => value);
    }

    clearBackofficePreference(key: string): void {
        this.ensureBackofficeData();
        this.updatePath(['backoffice', 'preferences'], (current: Record<string, string>) => {
            const updated = { ...current };
            delete updated[key];
            return updated;
        });
    }

    /**
     * Clear only backoffice data (or remove it entirely)
     */
    clearBackofficeData(): void {
        const storage = this.getStorage();
        if (storage.backoffice) {
            delete storage.backoffice;
            this.storage = storage;
            this.saveStorage();
            logger.debug('🧹 Cleared backoffice data');
        }
    }

    // ==================== PUBLIC API - UTILITY ====================

    /**
     * Clear all Rose storage data
     */
    clearAll(): void {
        // Deep copy to avoid mutating DEFAULT constants
        this.storage = {
            version: CURRENT_VERSION,
            widget: {
                settings: {
                    logLevel: null,
                    namedLogLevels: {},
                },
                analytics: {
                    lastSessionId: null,
                    lastActiveSessionId: null,
                    lastActiveSessionDate: null,
                    clientId: null,
                    apiVersion: null,
                    frontendVersion: null,
                },
                completedPostConversion: {},
                trafficControl: null,
                trafficControlOverride: null,
            },
        };
        this.saveStorage();
        logger.debug('🧹 Cleared all Rose storage');
    }

    /**
     * Clear only widget data
     */
    clearWidgetData(): void {
        // Deep copy to avoid mutating DEFAULT constants
        this.updatePath(['widget'], () => ({
            settings: {
                logLevel: null,
                namedLogLevels: {},
            },
            analytics: {
                lastSessionId: null,
                lastActiveSessionId: null,
                lastActiveSessionDate: null,
                clientId: null,
                apiVersion: null,
                frontendVersion: null,
            },
            trafficControl: null,
        }));
        logger.debug('🧹 Cleared widget data');
    }

    /**
     * Clear only preprod data (or remove it entirely)
     */
    clearPreprodData(): void {
        const storage = this.getStorage();
        if (storage.preprod) {
            delete storage.preprod;
            this.storage = storage;
            this.saveStorage();
            logger.debug('🧹 Cleared preprod data');
        }
    }

    /**
     * Get complete storage for debugging
     */
    getDebugInfo(): RoseStorage {
        return { ...this.getStorage() };
    }
}

// ==================== SINGLETON EXPORT ====================

export const roseStorage = new RoseStorageManager();
